<script>
import { formatAuthorValue } from "@/utils/format";
import formatDate from "@/utils/formatDate";
import {
  createRubric,
  getSingleRubric,
  getSingleRubricByShareSlug,
} from "@/api";
import RubricShareModal from "@/components/rubrics/rubric_share_modal.vue";
import RubricPreviewTables from "@/components/rubrics/rubric_preview_tables.vue";
import RubricFullscreenModal from "@/components/rubrics/components/rubric_fullscreen_modal.vue";
import { rubricCriteriaToCriteriaTable } from "@/utils/rubricCriteriaToCriteriaTable";
import { pick } from "lodash";
import { LSK_SUGGESTION_FORM_SELECTED_RUBRIC } from "@/constants";
import ConfirmationLeaveModal from "@/components/global/globalModals/confirmation_leave_modal";

const pageKinds = Object.freeze({
  share: Symbol(),
  preview: Symbol(),
});

export default {
  name: "PreviewRubricView",
  components: {
    RubricPreviewTables,
    RubricFullscreenModal,
  },
  props: {
    withHeader: {
      type: Boolean,
      default: true,
    },
    initialRubricId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      rubric: null,
      loading: true,
      pageKinds,
    };
  },
  computed: {
    rubricId() {
      return this.initialRubricId || this.$route.params.id;
    },

    isLoggedIn() {
      return this.$store.state.user?.id;
    },
    pageKind() {
      return this.$route.meta.isShareView ? pageKinds.share : pageKinds.preview;
    },

    rubricData() {
      return {
        id: this.rubric.id,
        name: this.rubric.name,
        type: this.rubric.type,
        intensity: this.rubric.intensity,
        criterias: this.rubric.criterias,
        LanguageId: this.rubric.language?.id,
        gradeLevel: this.rubric.gradeLevel,
        author: this.rubric.author,
      };
    },
  },
  async mounted() {
    await this.fetchRubric();
  },
  methods: {
    rubricCriteriaToCriteriaTable,
    formatAuthorValue,
    formatDate,
    async fetchRubric() {
      const fetch =
        this.pageKind === pageKinds.share
          ? () => getSingleRubricByShareSlug(this.$route.params.slug)
          : () => getSingleRubric(this.rubricId);

      try {
        const { data } = await fetch();

        this.rubric = data;

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$showToastError("Error while loading Rubric");
      }
    },
    belongsToCurrentUser(rubric) {
      return rubric.author && rubric.author.id === this.$store.state.user?.id;
    },
    handleCancel() {
      if (this.pageKind === pageKinds.share) {
        this.$router.push(this.isLoggedIn ? { name: "dashboard" } : "/login");
        return;
      }
      this.$egModal.hide("modal-rubric-preview");
    },
    async handleEdit() {
      const isRubrics = this.$route.name === "rubrics";
      const isManualGrading = this.$route.name === "manual-grading";

      const canEdit =
        isRubrics ||
        isManualGrading ||
        (!isRubrics &&
          !isManualGrading &&
          (await this.$egModal.show("confirmation-leave-modal", {
            component: ConfirmationLeaveModal,
          })));

      if (this.rubric && canEdit) {
        await this.$router.push({
          name: "edit-rubric",
          params: { id: this.rubric.id },
        });
        await this.$egModal.hideAll();
      }
    },
    handleShare() {
      this.$egModal.show("rubric-share-modal", {
        component: RubricShareModal,
        props: {
          rubric: this.rubric,
          zIndex: 1060,
          showBackdrop: true,
        },
      });
    },
    async useRubric() {
      if (this.isLoggedIn) {
        localStorage.setItem(
          LSK_SUGGESTION_FORM_SELECTED_RUBRIC,
          JSON.stringify(this.rubric)
        );
        await this.$router.push({ path: "/grading/manual-upload" });
      } else {
        localStorage.setItem(
          "share:useRubricData",
          JSON.stringify(this.rubricData)
        );
        await this.$router.push("/login");
      }
    },
    async saveAndEdit() {
      const createRubricData = pick(this.rubricData, [
        "name",
        "type",
        "intensity",
        "criterias",
        "LanguageId",
        "gradeLevel",
      ]);
      if (this.isLoggedIn) {
        const { data } = await createRubric(createRubricData);
        await this.$router.push(`/rubric/edit/${data.id}?redirectTo=rubrics`);
      } else {
        localStorage.setItem(
          "share:newRubricData",
          JSON.stringify(createRubricData)
        );
        await this.$router.push("/login");
      }
    },
  },
};
</script>

<template>
  <RubricFullscreenModal
    :mode="pageKind === pageKinds.share ? 'share' : 'preview'"
    :rubric-name="rubric?.name || ''"
    :is-system="rubric?.isSystem || 0"
    :rubric-author="rubric?.author || null"
    hide-footer
    @saveAndEdit="saveAndEdit"
    @gradeImmediately="useRubric"
    @cancel="handleCancel"
    @edit="handleEdit"
    @share="handleShare"
  >
    <section v-if="loading" class="loading-section">
      <div class="text-center">
        <eg-b-spinner class="mb-3"></eg-b-spinner>
        <p>{{ $t("common.processingRequest") }}</p>
      </div>
    </section>

    <RubricPreviewTables
      v-else-if="!!rubric"
      :type="rubric.type"
      :criteria="rubricCriteriaToCriteriaTable(rubric.criterias, rubric.type)"
    />
  </RubricFullscreenModal>
</template>

<style scoped lang="scss">
.loading-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 116px);
}
</style>
